import { AuthErrorCode } from '@axys-notes/common';
import { TeamsContext } from '../providers/teams-context-provider';
import { ALL_SCOPES } from '../constants/scopes';

const MAX_ATTEMPTS = 3;

/**
 * Error thrown for a non-OK API response. `code` is set when the gateway
 * answered with a known auth error code; `consent` is the pending consent
 * flow started for a `consent-required` answer (resolves once granted).
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: AuthErrorCode,
    readonly consent?: Promise<void>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const toAuthErrorCode = (message: unknown): AuthErrorCode | undefined => {
  if (typeof message !== 'string') return undefined;
  return Object.values(AuthErrorCode).find((code) => message.includes(code));
};

export const getAuthErrorCode = (error: unknown): AuthErrorCode | undefined =>
  error instanceof ApiError
    ? error.code
    : error instanceof Error
      ? toAuthErrorCode(error.message)
      : undefined;

export const handleRetry = (
  failureCount: number,
  error: Error,
  // Kept for call-site compatibility.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  teamsContext: TeamsContext
) => {
  if (getAuthErrorCode(error)) {
    return false;
  }
  // failureCount is 0 on the first failure: at most MAX_ATTEMPTS attempts.
  return failureCount < MAX_ATTEMPTS - 1;
};

export const handleErrorResponse = async (
  response: Response,
  teamsContext: TeamsContext
) => {
  if (response.ok) {
    return;
  }

  let message: string = response.statusText;
  try {
    const json = await response.json();
    if (typeof json?.message === 'string') {
      message = json.message;
    }
  } catch {
    // Not a JSON body: keep the status text.
  }

  const code = toAuthErrorCode(message);
  let consent: Promise<void> | undefined;

  if (
    code === AuthErrorCode.ConsentRequired &&
    teamsContext.shouldRequestScopedConsent
  ) {
    // Request consent for all scopes (base + optional like OneDrive). The
    // provider de-duplicates: while a request is pending, every caller gets
    // the same promise, so a response arriving with the popup open waits too.
    consent = teamsContext.shouldRequestScopedConsent(ALL_SCOPES);
    // Callers that do not replay must not trigger an unhandled rejection.
    consent.catch(() => undefined);
  } else if (message.includes('jwt expired') && teamsContext.renewAuthCode) {
    await teamsContext.renewAuthCode();
  }
  // reauth-required: no popup, the stored session cannot be fixed by consent.

  throw new ApiError(message, response.status, code, consent);
};

/** Readable French message for a failed API call. */
export const describeApiError = (error: unknown, fallback: string): string => {
  switch (getAuthErrorCode(error)) {
    case AuthErrorCode.ReauthRequired:
      return 'Votre session Microsoft doit être renouvelée. Fermez puis rouvrez Axys Notes dans Teams, puis réessayez.';
    case AuthErrorCode.ConsentRequired:
      // Also reached right after a granted consent (cooldown): stay neutral.
      return "L'autorisation n'est pas encore active. Réessayez dans quelques instants ; si le problème persiste, acceptez la demande d'autorisation.";
    default:
      return fallback;
  }
};
